import { baseKeymap } from "prosemirror-commands";
import { dropCursor } from "prosemirror-dropcursor";
import { buildInputRules, buildKeymap } from "prosemirror-example-setup";
import { gapCursor } from "prosemirror-gapcursor";
import { history } from "prosemirror-history";
import { keymap } from "prosemirror-keymap";
import { nodes as basicNodes } from "prosemirror-schema-basic";
import { Plugin } from "prosemirror-state";
import { changeParentNode, updateNodeAttributes, } from "../../commands/commands";
import { Extension } from "../../core/extension";
import { paragraphNodeSpec } from "./paragraphNodeSpec";
import { normalizeNestedParagraphs } from "./paste-rules";
export const defaultOptions = {
    docContent: "block+",
    hardBreak: true,
    enableParagraphAlignment: true,
};
/**
 * A base extension that add base nodes and commands.
 * Add nodes: doc, paragraph, text and hard_break
 */
export class BaseSchemaExtension extends Extension {
    constructor(options = {}) {
        super();
        this.options = { ...defaultOptions, ...options };
    }
    addNodes(nodes) {
        const { docContent, hardBreak } = this.options;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const { doc, text, hard_break } = basicNodes;
        let augmentedNodes = nodes.append({
            doc: { ...doc, content: docContent },
            ...paragraphNodeSpec,
            text,
        });
        if (hardBreak) {
            augmentedNodes = augmentedNodes.append({ hard_break });
        }
        return augmentedNodes;
    }
    addCommands(schema) {
        return {
            applyParagraph: changeParentNode(schema.nodes.paragraph),
            applyParagraphWithAttributes: (attrs) => changeParentNode(schema.nodes.paragraph, attrs),
            ...(this.options.enableParagraphAlignment
                ? {
                    applyTextAlignment: (textAlign) => updateNodeAttributes({ textAlign }),
                }
                : {}),
        };
    }
    addPlugins(schema, plugins, { commands }) {
        return plugins.concat([
            new Plugin({
                view: () => ({
                    update: (view) => {
                        view.commands = commands;
                    },
                }),
            }),
            normalizeNestedParagraphs(),
        ]);
    }
}
/**
 * A base extension that adds the plugins provided by `prosemirror-example-setup`.
 *
 * It includes plugins with inputRules and keymap commands for nodes and marks added by `BaseSchemaExtension`.
 */
export class BasePluginsExtension extends Extension {
    constructor(options = {}) {
        super();
        this.options = options;
    }
    addPlugins(schema, plugins) {
        const basePlugins = [
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            buildInputRules(schema),
            keymap(
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call
            buildKeymap(schema, {
                Escape: false,
                // these commands are added by their own extensions, so we disable here:
                "Mod-b": false,
                "Mod-B": false,
                "Mod-i": false,
                "Mod-I": false,
                "Mod-`": false,
                "Mod-_": false,
            })),
            keymap(baseKeymap),
            dropCursor(),
            gapCursor(),
        ];
        if (!this.options.disableHistory) {
            basePlugins.push(history({ depth: 50, newGroupDelay: 800 }));
        }
        return plugins.concat(basePlugins);
    }
}
